import { IMemberLevelDetail } from '@/model/types/member'

export interface AddMemberLevelValues {
    name: string
    experience_threshold: number
    experience_deduction?: number
    expiration: number
}

export interface AddMemberLevelParams extends AddMemberLevelValues {
    experience_deduction: number
    level: number
    benefits?: number[]
    discount_rate?: number
    booster_multiplier?: number
}

const BENEFIT_MEMBER_PRICE = 1
const BENEFIT_MEMBER_DISCOUNT = 2
const BENEFIT_POINT_BOOSTER = 3

export const getMaxMemberLevel = (levels: IMemberLevelDetail[]) =>
    levels.reduce<number | undefined>((maxLevel, item) => {
        if (maxLevel === undefined) return item.level

        return item.level > maxLevel ? item.level : maxLevel
    }, undefined)

export const canDeleteMemberLevel = (level: IMemberLevelDetail, levels: IMemberLevelDetail[]) => {
    if (levels.length <= 1) return false

    return level.level === getMaxMemberLevel(levels)
}

export const isSameLevelConfig = (current: IMemberLevelDetail, origin?: IMemberLevelDetail) =>
    !!origin &&
    current.name === origin.name &&
    Number(current.experience_threshold) === Number(origin.experience_threshold) &&
    Number(current.experience_deduction || 0) === Number(origin.experience_deduction || 0) &&
    Number(current.expiration) === Number(origin.expiration)

export const getChangedMemberLevels = (levels: IMemberLevelDetail[], originLevels: IMemberLevelDetail[]) =>
    levels.filter(
        level =>
            !isSameLevelConfig(
                level,
                originLevels.find(origin => origin.id === level.id),
            ),
    )

export const MEMBER_LEVEL_NAMES = ['青铜会员', '白银会员', '黄金会员', '紫钻会员', '星耀会员']

export const getMemberLevelName = (level: number) =>
    Number.isInteger(level) && level >= 0 && level < MEMBER_LEVEL_NAMES.length
        ? MEMBER_LEVEL_NAMES[level]
        : `等级 ${Number(level) + 1}`

export const getNextMemberLevelName = (levels: IMemberLevelDetail[]) => {
    if (levels.length >= MEMBER_LEVEL_NAMES.length) return undefined
    const existingLevels = new Set(levels.map(item => item.level))
    if (existingLevels.size !== levels.length || levels.some((_, index) => !existingLevels.has(index))) {
        return undefined
    }

    return MEMBER_LEVEL_NAMES[levels.length]
}

export const buildMemberLevelUpdate = (level: IMemberLevelDetail, origin: IMemberLevelDetail) => ({
    id: level.id,
    ...(level.name !== origin.name ? { name: (level.name || '').trim() } : {}),
    ...(Number(level.experience_threshold) !== Number(origin.experience_threshold)
        ? { experience_threshold: Number(level.experience_threshold) }
        : {}),
    ...(Number(level.experience_deduction || 0) !== Number(origin.experience_deduction || 0)
        ? { experience_deduction: Number(level.experience_deduction || 0) }
        : {}),
    ...(Number(level.expiration) !== Number(origin.expiration) ? { expiration: Number(level.expiration) } : {}),
})

export const validateMemberLevelName = (
    name?: string,
    existingNames: string[] = [],
    options: { skipFormatRules?: boolean } = {},
): string => {
    const trimmed = (name || '').trim()
    if (!trimmed) {
        return '会员等级名称不能为空'
    }
    // 历史等级名可能超过 4 字（后端口径为 20 字），未改名时不能挡住门槛/有效期保存
    if (!options.skipFormatRules) {
        if (trimmed.length > 4) {
            return '会员等级名称不能超过4个字符'
        }
        if (!/^[\u4e00-\u9fa5a-zA-Z0-9]+$/.test(trimmed)) {
            return '会员等级名称只允许中文、英文字母、数字'
        }
    }
    const normalized = trimmed.toLowerCase()
    if (existingNames.some(item => (item || '').trim().toLowerCase() === normalized)) {
        return '会员等级名称重复'
    }

    return ''
}

/** 保存单行时校验名称：未改名（可能是历史长名称）只校验空值和重名 */
export const validateMemberLevelNameForSave = (
    name: string | undefined,
    originName: string | undefined,
    existingNames: string[] = [],
) => validateMemberLevelName(name, existingNames, { skipFormatRules: (name || '') === (originName || '') })

export const getHighestMemberLevel = (levels: IMemberLevelDetail[]) =>
    levels.reduce<IMemberLevelDetail | undefined>((highest, item) => {
        if (!highest) return item

        return item.level > highest.level ? item : highest
    }, undefined)

export const buildAddMemberLevelParams = (
    values: AddMemberLevelValues,
    levels: IMemberLevelDetail[],
): AddMemberLevelParams => {
    const highestLevel = getHighestMemberLevel(levels)
    const benefits = highestLevel?.benefits || {}
    const memberPriceBenefit = benefits.benefit_member_price
    const memberDiscountBenefit = benefits.benefit_member_discount
    const pointBoosterBenefit = benefits.benefit_point_booster
    const configurableBenefits = [
        ...(memberPriceBenefit?.if_has ? [BENEFIT_MEMBER_PRICE] : []),
        ...(memberDiscountBenefit?.if_has ? [BENEFIT_MEMBER_DISCOUNT] : []),
        ...(pointBoosterBenefit?.if_has ? [BENEFIT_POINT_BOOSTER] : []),
    ]
    const params: AddMemberLevelParams = {
        name: values.name,
        experience_threshold: values.experience_threshold,
        experience_deduction: values.experience_deduction || 0,
        level: highestLevel ? highestLevel.level + 1 : 0,
        expiration: values.expiration,
    }

    if (!configurableBenefits.length) return params

    return {
        ...params,
        benefits: configurableBenefits,
        ...(memberDiscountBenefit?.if_has ? { discount_rate: memberDiscountBenefit.discount_rate } : {}),
        ...(pointBoosterBenefit?.if_has ? { booster_multiplier: pointBoosterBenefit.multiplier } : {}),
    }
}

export const validateMemberLevels = (levels: IMemberLevelDetail[]) => {
    const sorted = [...levels].sort((a, b) => a.level - b.level)

    for (let index = 0; index < sorted.length; index++) {
        if (sorted[index].experience_threshold < 0) return '成长值门槛不能小于0'
        if (sorted[index].experience_deduction < 0) return '成长值扣减不能小于0'
        if (index === 0 && Number(sorted[index].experience_threshold) !== 0) {
            return '第一个会员等级成长值门槛必须为0'
        }
        if (index > 0 && sorted[index].experience_threshold <= sorted[index - 1].experience_threshold) {
            return '成长值门槛必须按等级严格递增'
        }
    }

    return ''
}
